<template>
	<div class="OrganisationFilter">
		<Multiselect
			:options="multiselectOptions"
			:model-value="internalValue"
			:mode="MultiselectMode.TAGS"
			:searchable="true"
			:can-clear="true"
			:max-height="'18rem'"
			:placeholder="placeholder"
			@updateValues="handleMultiselectUpdate"
		/>

		<div class="List">
			<CheckBoxSelectItem
				v-for="item in organisationItems"
				:key="item.value"
				:item="item"
				@selectionChange="(state) => handleSelectionChange(item, state)"
			/>
		</div>
	</div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import { storeToRefs } from 'pinia';
import Multiselect from '@components/Multiselect/Multiselect.vue';
import CheckBoxSelectItem from '@components/CheckBoxSelectItem/CheckBoxSelectItem.vue';
import { MultiselectMode } from '@app/enums/MultiselectMode';
import { SelectListItemStateType } from '@domain/enums/SelectListItemStateType';
import type ISelectItem from '@domain/interfaces/dtos/ISelectItem';
import type { IExtendedFilterValue } from '@app/interfaces/dtos/IExtendedFilterValue';
import type { IMultiselectOption } from '@app/interfaces/dtos/IMultiselectOption';
import { FilterType } from '@domain/enums/FilterType';
import { FILTER_TO_FACETS_MAPPER } from '@domain/mappers/FilterToFacetsMapper';
import { useSearchStore } from '@stores/useSearchStore';

const router = useRouter();
const searchStore = useSearchStore();
const { filtersSelection, pageData } = storeToRefs(searchStore);

const placeholder = 'Search by University';

// TODO Decide top organisations based on user data or popularity?
const organisations = ref<IExtendedFilterValue[]>([
	{ id: 'university-of-california', name: 'University of California' },
	{ id: 'university-of-oxford', name: 'University of Oxford' },
	{ id: 'university-of-texas', name: 'University of Texas' },
	{ id: 'university-of-utah', name: 'University of Utah' },
	{ id: 'university-of-washington', name: 'University of Washington' }
]);

const multiselectOptions = ref<IMultiselectOption[]>([]);
// TODO: Fetch organisation options for the multiselect from the SearchAPI.

const selectedOrganisationIds = computed(() =>
	filtersSelection.value.find((f) => f.type === FilterType.ORGANISATIONS)?.values.map((v) => v.id) ?? []
);

const selectedIdsSet = computed(() => new Set(selectedOrganisationIds.value));

const internalValue = ref<(string | number)[]>([...selectedOrganisationIds.value]);

watch(selectedOrganisationIds, (ids) => {
	const currentSet = new Set(internalValue.value);
	const newSet = new Set(ids);
	const isSame = currentSet.size === newSet.size && ids.every((id) => currentSet.has(id));
	if (!isSame) {
		internalValue.value = [...ids];
	}
});

const facetCounts = computed(() => {
	const counts = new Map<string, number>();
	const facets = pageData.value?.filtersFacets ?? {};
	const key = FILTER_TO_FACETS_MAPPER.get(FilterType.ORGANISATIONS);

	if (key && facets[key]) {
		(facets as any)[key]?.forEach((facet: { value: string; count: number }) => counts.set(facet.value, facet.count));
	}

	return counts;
});

const organisationItems = computed<ISelectItem[]>(() =>
	organisations.value.map((organisation) => ({
		value: organisation.id,
		text: organisation.name ?? organisation.id,
		count: facetCounts.value.get(organisation.id),
		state: selectedIdsSet.value.has(organisation.id)
			? SelectListItemStateType.SELECTED
			: SelectListItemStateType.ENABLED
	}))
);

const updateSelection = async (organisationId: string, shouldSelect: boolean): Promise<void> => {
	const organisationValue = organisations.value.find((organisation) => organisation.id === organisationId);
	if (!organisationValue) return;

	const currentSelection = filtersSelection.value.find((filter) => filter.type === FilterType.ORGANISATIONS);
	const alreadySelected = currentSelection?.values.some((value) => value.id === organisationValue.id) ?? false;

	if (shouldSelect && alreadySelected) return;
	if (!shouldSelect && !alreadySelected) return;

	let nextFilters = [...filtersSelection.value];

	if (shouldSelect) {
		if (currentSelection) {
			const updatedSelection = { ...currentSelection, values: [...currentSelection.values, organisationValue] };
			nextFilters = nextFilters.map((filter) => filter.type === FilterType.ORGANISATIONS ? updatedSelection : filter);
		} else {
			nextFilters = [...nextFilters, { type: FilterType.ORGANISATIONS, values: [organisationValue] }];
		}
	} else if (currentSelection) {
		const remainingValues = currentSelection.values.filter((value) => value.id !== organisationValue.id);
		if (remainingValues.length > 0) {
			const updatedSelection = { ...currentSelection, values: remainingValues };
			nextFilters = nextFilters.map((filter) => filter.type === FilterType.ORGANISATIONS ? updatedSelection : filter);
		} else {
			nextFilters = nextFilters.filter((filter) => filter.type !== FilterType.ORGANISATIONS);
		}
	}

	searchStore.filtersSelection = nextFilters;

	try {
		await searchStore.setRouteFromFilters(router);
		await searchStore.loadPageData(router);
	} catch (error) {
		// TODO : Handle error properly
		console.error('Failed to update organisation filter', error);
	}
};

const handleSelectionChange = async (item: ISelectItem, state: string): Promise<void> => {
	await updateSelection(String(item.value), state === SelectListItemStateType.SELECTED);
};

const handleMultiselectUpdate = async (value: (string | number)[] | string | number | null): Promise<void> => {
	const normalized = Array.isArray(value) ? value : value ? [value] : [];
	const prevSet = new Set(internalValue.value);
	const nextSet = new Set(normalized);

	const added = normalized.filter((v) => !prevSet.has(v));
	const removed = internalValue.value.filter((v) => !nextSet.has(v));

	for (const add of added) {
		await updateSelection(String(add), true);
	}
	for (const rem of removed) {
		await updateSelection(String(rem), false);
	}
};
</script>

<style scoped lang="scss">
.OrganisationFilter {
	display: flex;
	flex-direction: column;
	width: 100%;
	gap: 1.5rem;
	padding: 0 2px;	// required for outline not to be cut off

	.List {
		display: flex;
		flex-direction: column;
		overflow: auto;
		scrollbar-width: none;
		padding: 2px 2px 0 0;	// required for outline not to be cut off

		&::-webkit-scrollbar {
			display: none;
		}
	}
}
</style>
